// Админ › Нова ниша — vertical × city × areas → matrix → draft → approve (plan §4.3).
//
// The generator runs here, in the browser, on every change of the inputs; it is pure and cheap.
// Nothing reaches the database before "Запази като чернова", and nothing runs before "Одобри".

import { useMemo, useState } from 'react';
import {
  buildQueryMatrix,
  estimateMatrix,
  TIERS,
  type MatrixEstimate,
  type MatrixResult,
  type QueryCandidate,
  type QueryTier,
} from '@leadg/shared';
import { useSettings } from '../useAdmin.js';
import { useVerticals, type VerticalRow } from '../verticals/useVerticals.js';
import { useCities, useCityAreas, type CityAreaRow, type CityRow } from '../cities/useCities.js';
import { readCap, readTierCooldowns, toTerritory, toVocabulary } from './matrix.js';
import { useExistingQueryTexts } from './useWizard.js';
import { StepAreas, StepCity, StepVertical } from './WizardPickers.js';
import { StepMatrix } from './StepMatrix.js';
import { StepSave } from './StepSave.js';

const STEPS = ['Ниша', 'Град', 'Райони', 'Матрица', 'Запис', 'Готово'] as const;

function toggled<T>(set: ReadonlySet<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

function StepIndicator({ step, onJump }: { step: number; onJump: (n: number) => void }) {
  return (
    <ol className="adm__steps">
      {STEPS.map((label, index) => {
        const n = index + 1;
        const on = n === step || (step === 5 && n === 6);
        const cls = `adm__step ${on ? 'adm__step--on' : ''} ${n < step ? 'adm__step--done' : ''}`;
        return (
          <li key={label} className={cls}>
            <button type="button" disabled={n > step} onClick={() => onJump(Math.min(n, 5))}>
              {n}. {label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

interface WizardState {
  verticals: VerticalRow[];
  cities: CityRow[];
  areas: CityAreaRow[];
  vertical: VerticalRow | null;
  city: CityRow | null;
  verticalKey: string | null;
  cityKey: string | null;
  ticked: ReadonlySet<number>;
  tiers: ReadonlySet<QueryTier>;
  unticked: ReadonlySet<string>;
  result: MatrixResult | null;
  selected: QueryCandidate[];
  estimate: MatrixEstimate;
  cap: number;
}

interface WizardActions {
  pickVertical: (key: string) => void;
  pickCity: (key: string) => void;
  setTicked: (next: Set<number>) => void;
  toggleTier: (tier: QueryTier) => void;
  toggleRow: (text: string) => void;
}

function StepBody({ step, s, a }: { step: number; s: WizardState; a: WizardActions }) {
  switch (step) {
    case 1:
      return <StepVertical verticals={s.verticals} value={s.verticalKey} onPick={a.pickVertical} />;
    case 2:
      return <StepCity cities={s.cities} value={s.cityKey} onPick={a.pickCity} />;
    case 3:
      return (
        <StepAreas
          areas={s.areas}
          ticked={s.ticked}
          onToggle={(id) => a.setTicked(toggled(s.ticked, id))}
          onSetAll={(on) => a.setTicked(on ? new Set(s.areas.map((x) => x.id)) : new Set())}
        />
      );
    case 4:
      return s.result === null ? null : (
        <StepMatrix
          result={s.result}
          estimate={s.estimate}
          cap={s.cap}
          tiers={s.tiers}
          unticked={s.unticked}
          onToggleTier={a.toggleTier}
          onToggleRow={a.toggleRow}
        />
      );
    default:
      return s.vertical === null || s.city === null ? null : (
        <StepSave
          key={`${s.vertical.key}-${s.city.key}`}
          selected={s.selected}
          vertical={s.vertical}
          city={s.city}
          otherActive={s.verticals
            .filter((v) => v.active && v.key !== s.vertical?.key)
            .map((v) => v.name_bg)}
        />
      );
  }
}

export function NicheWizard() {
  const verticalsQuery = useVerticals();
  const citiesQuery = useCities();
  const settingsQuery = useSettings();
  const existingQuery = useExistingQueryTexts();

  const [step, setStep] = useState(1);
  const [verticalKey, setVerticalKey] = useState<string | null>(null);
  const [cityKey, setCityKey] = useState<string | null>(null);
  const [tickedState, setTicked] = useState<Set<number> | null>(null);
  const [tiers, setTiers] = useState<ReadonlySet<QueryTier>>(() => new Set(TIERS));
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(() => new Set());
  const areasQuery = useCityAreas(cityKey);

  const verticals = useMemo(() => verticalsQuery.data ?? [], [verticalsQuery.data]);
  const cities = useMemo(() => citiesQuery.data ?? [], [citiesQuery.data]);
  const areas = useMemo(() => areasQuery.data ?? [], [areasQuery.data]);
  const settings = useMemo(() => settingsQuery.data ?? [], [settingsQuery.data]);
  const existing = useMemo(() => existingQuery.data ?? [], [existingQuery.data]);
  const vertical = useMemo(
    () => verticals.find((v) => v.key === verticalKey) ?? null,
    [verticals, verticalKey],
  );
  const city = useMemo(() => cities.find((c) => c.key === cityKey) ?? null, [cities, cityKey]);

  const ticked = useMemo(
    () => tickedState ?? new Set(areas.filter((x) => x.include_in_queries).map((x) => x.id)),
    [tickedState, areas],
  );

  const result = useMemo(() => {
    if (vertical === null || city === null) return null;
    return buildQueryMatrix(toVocabulary(vertical), toTerritory(city, areas, ticked), {
      tierCooldowns: readTierCooldowns(settings),
      existing,
    });
  }, [vertical, city, areas, ticked, settings, existing]);

  const selected = useMemo(
    () => (result?.candidates ?? []).filter((c) => tiers.has(c.tier) && !unticked.has(c.queryText)),
    [result, tiers, unticked],
  );
  const cap = readCap(settings);
  const estimate = useMemo(() => estimateMatrix(selected, cap), [selected, cap]);

  const canAdvance = [true, verticalKey !== null, cityKey !== null, true, selected.length > 0][
    step
  ];

  if (verticalsQuery.isLoading || citiesQuery.isLoading) {
    return <p className="adm__msg">Зареждане…</p>;
  }

  const state: WizardState = {
    verticals,
    cities,
    areas,
    vertical,
    city,
    verticalKey,
    cityKey,
    ticked,
    tiers,
    unticked,
    result,
    selected,
    estimate,
    cap,
  };
  const actions: WizardActions = {
    pickVertical: setVerticalKey,
    pickCity: (key) => {
      setCityKey(key);
      setTicked(null);
      setUnticked(new Set());
    },
    setTicked,
    toggleTier: (tier) => setTiers(toggled(tiers, tier)),
    toggleRow: (text) => setUnticked(toggled(unticked, text)),
  };

  return (
    <div className="adm__wizard">
      <div className="adm__section-header">
        <h3 className="adm__h">Нова ниша</h3>
        <span className="adm__sub">
          Матрицата се строи тук и се преглежда, преди да струва каквото и да е.
        </span>
      </div>
      <StepIndicator step={step} onJump={setStep} />
      <StepBody step={step} s={state} a={actions} />
      <div className="adm__actions adm__wizard-nav">
        <button
          type="button"
          className="adm__json-btn"
          disabled={step === 1}
          onClick={() => setStep(step - 1)}
        >
          ← Назад
        </button>
        {step < 5 ? (
          <button
            type="button"
            className="adm__resolve-btn"
            disabled={!canAdvance}
            onClick={() => setStep(step + 1)}
          >
            Напред →
          </button>
        ) : null}
      </div>
    </div>
  );
}
